import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  ManageEntitiesActionType,
  useManageEntitiesContext,
} from "../../../context/ManageEntitiesContext";
import useAlert from "../../../hooks/useAlert";
import { AddEntity } from "../../../models/entity.interface";
import organizationService from "../../../services/organization.service";
import { isNumber } from "../../../utils/sharedUtil";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import DeleteWarningModal from "../../Shared/DeleteWarningModal/DeleteWarningModal";
import { Loader } from "@shltelemedicine/shl-ui-lib";
import AddEntityModal from "./AddEntityModal/AddEntityModal";
import styles from "./ManageEntities.module.css";
import ManageEntitiesHeader from "./ManageEntitiesHeader/ManageEntitiesHeader";
import ManageEntitiesTree from "./ManageEntitiesTree/ManageEntitiesTree";

const ManageEntities = () => {
  const {
    state: {
      loading,
      entities,
      selectedEntity,
      entityModal: { show: showEntityModal, mode: entityModalMode },
      showDeleteEntityModal,
      totalEntities,
      totalUsers,
      totalActiveUsers,
    },
    dispatch,
    loadEntities,
  } = useManageEntitiesContext();

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      await loadEntities();
    })();
  }, []); // eslint-disable-line

  const saveEntityHandler = async (
    selectedEntityId: string,
    entityDetails: AddEntity | Partial<AddEntity>
  ) => {
    const isAdd = entityModalMode === "add";

    const { success, errorCode } = isAdd
      ? await organizationService.addEntity(
          selectedEntity!.id,
          entityDetails as AddEntity
        )
      : await organizationService.updateEntity({
          id: selectedEntityId,
          ...entityDetails,
        });

    if (!success) {
      const { message, support } = getAlertMessageAndSupportContent(
        isAdd ? "ERROR.ADD_ENTITY" : "ERROR.EDIT_ENTITY",
        isAdd
          ? "An error occurred while trying to add an entity.<br></br>Please try again."
          : "An error occurred while updating an entity.<br></br>Please try again.",
        {
          br: () => <br></br>,
        },
        errorCode
      );
      showErrorAlert(message, support);
      return;
    }

    closeAddEntityModal();

    await loadEntities();
  };

  const deleteEntityHandler = async () => {
    setDeleting(true);

    try {
      const { success, errorCode } = await organizationService.deleteEntity(
        selectedEntity!.id
      );

      if (!success) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.DELETE_ENTITY",
          "An error occurred while trying to delete an entity.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      dispatch({
        type: ManageEntitiesActionType.SET_DELETE_ENTITY_MODAL,
        payload: false,
      });

      await loadEntities();
    } finally {
      setDeleting(false);
    }
  };

  const closeAddEntityModal = () => {
    dispatch({
      type: ManageEntitiesActionType.SET_ENTITY_MODAL,
      payload: {
        mode: entityModalMode,
        show: false,
      },
    });
  };

  const closeDeleteEntityModal = () => {
    dispatch({
      type: ManageEntitiesActionType.SET_DELETE_ENTITY_MODAL,
      payload: false,
    });
  };

  const showEmptyOrgInfo =
    entities.length === 1 &&
    (!entities[0].childNodes || !entities[0].childNodes.length);

  const inactiveUsers =
    isNumber(totalUsers) && isNumber(totalActiveUsers)
      ? totalUsers! - totalActiveUsers!
      : undefined;

  return (
    <>
      <ManageEntitiesHeader
        totalEntities={totalEntities}
        users={{
          total: totalUsers,
          active: totalActiveUsers,
          inactive: inactiveUsers,
        }}
      />
      <div className="p-4">
        {loading ? (
          <Loader />
        ) : (
          <>
            <ManageEntitiesTree data={entities} />
            {showEmptyOrgInfo && (
              <div className={`${styles.info} font-md px-4`}>
                <FormattedMessage
                  id="MANAGEMENT.EMPTY_ENTITIES"
                  defaultMessage="Start by creating a new entity and assigning users to it"
                />
              </div>
            )}
          </>
        )}
      </div>
      <AddEntityModal
        show={showEntityModal}
        mode={entityModalMode ?? "add"}
        entity={selectedEntity ?? undefined}
        onSave={saveEntityHandler}
        onClose={closeAddEntityModal}
        parentName={
          entityModalMode === "add" ? selectedEntity?.name : undefined
        }
      />
      <DeleteWarningModal
        show={showDeleteEntityModal}
        loading={deleting}
        onClose={closeDeleteEntityModal}
        onConfirm={deleteEntityHandler}
      >
        <FormattedMessage
          id="MANAGEMENT.ENTITY.REMOVE"
          defaultMessage="You requested to delete an entity."
        />
      </DeleteWarningModal>

      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};
export default ManageEntities;
